import { motion } from 'framer-motion';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { BarChart, Bar, LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, PieChart, Pie, Cell } from 'recharts';
import { Trophy, Award, TrendingUp, DownloadCloud, Home } from 'lucide-react';
import { ExamResult, Exam } from '@/types/exam';

interface ResultsPageProps {
  exam: Exam;
  result: ExamResult;
  classRankings: ExamResult[];
  onDone: () => void;
}

export default function ResultsPage({
  exam,
  result,
  classRankings,
  onDone,
}: ResultsPageProps) {
  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: { staggerChildren: 0.1, delayChildren: 0.2 },
    },
  };

  const itemVariants = {
    hidden: { opacity: 0, y: 20 },
    visible: { opacity: 1, y: 0, transition: { duration: 0.5 } },
  };

  // Mock performance history data
  const performanceHistory = [
    { exam: 'Math 1', score: 65 },
    { exam: 'English 1', score: 78 },
    { exam: 'Science 1', score: 72 },
    { exam: 'Math 2', score: 82 },
    { exam: 'English 2', score: result.percentage },
  ];

  // Score distribution in class
  const scoreDistribution = [
    { name: 'A (80-100)', value: 12, fill: '#10b981' },
    { name: 'B (70-79)', value: 18, fill: '#3b82f6' },
    { name: 'C (60-69)', value: 25, fill: '#f59e0b' },
    { name: 'D (50-59)', value: 10, fill: '#ef4444' },
    { name: 'F (<50)', value: 5, fill: '#6b7280' },
  ];

  const getGradeColor = (percentage: number) => {
    if (percentage >= 80) return 'bg-green-100 text-green-800 border-green-300';
    if (percentage >= 70) return 'bg-blue-100 text-blue-800 border-blue-300';
    if (percentage >= 60) return 'bg-yellow-100 text-yellow-800 border-yellow-300';
    return 'bg-red-100 text-red-800 border-red-300';
  };

  const getGrade = (percentage: number) => {
    if (percentage >= 80) return 'A';
    if (percentage >= 70) return 'B';
    if (percentage >= 60) return 'C';
    return 'F';
  };

  return (
    <div className="min-h-screen bg-gradient-to-b from-primary/5 to-background py-8 px-4">
      <div className="max-w-6xl mx-auto">
        {/* Header */}
        <motion.div
          initial="hidden"
          animate="visible"
          variants={containerVariants}
          className="text-center space-y-4 mb-8"
        >
          <motion.div variants={itemVariants}>
            <h1 className="text-4xl font-bold text-primary">Exam Completed!</h1>
            <p className="text-muted-foreground mt-2">{exam.name} Results</p>
          </motion.div>
        </motion.div>

        {/* Main Score Card */}
        <motion.div
          initial="hidden"
          animate="visible"
          variants={containerVariants}
          className="grid grid-cols-1 lg:grid-cols-3 gap-6 mb-8"
        >
          {/* Score Display */}
          <motion.div variants={itemVariants}>
            <Card className="p-8 border-border bg-gradient-to-br from-primary/10 to-transparent">
              <div className="space-y-6 text-center">
                <div className="relative w-32 h-32 mx-auto">
                  <div className="absolute inset-0 flex items-center justify-center">
                    <div className="text-center">
                      <p className="text-5xl font-bold text-primary">
                        {result.percentage}
                      </p>
                      <p className="text-sm text-muted-foreground">%</p>
                    </div>
                  </div>
                  <svg className="w-full h-full transform -rotate-90">
                    <circle
                      cx="64"
                      cy="64"
                      r="56"
                      fill="none"
                      stroke="currentColor"
                      strokeWidth="8"
                      className="text-border"
                    />
                    <circle
                      cx="64"
                      cy="64"
                      r="56"
                      fill="none"
                      stroke="currentColor"
                      strokeWidth="8"
                      strokeDasharray={`${351.86 * (result.percentage / 100)} 351.86`}
                      className="text-secondary transition-all duration-1000"
                    />
                  </svg>
                </div>

                <div>
                  <Badge
                    className={`text-lg px-4 py-2 ${getGradeColor(result.percentage)}`}
                  >
                    Grade: {getGrade(result.percentage)}
                  </Badge>
                </div>

                <div className="space-y-2">
                  <p className="text-sm text-muted-foreground">Total Score</p>
                  <p className="text-3xl font-bold text-primary">
                    {result.marksObtained}/{result.totalMarks}
                  </p>
                </div>
              </div>
            </Card>
          </motion.div>

          {/* Position & Stats */}
          <motion.div variants={itemVariants} className="space-y-6">
            <Card className="p-6 border-border bg-gradient-to-br from-accent/10 to-transparent">
              <div className="flex items-center gap-3 mb-4">
                <Trophy className="w-6 h-6 text-accent" />
                <h3 className="font-semibold text-foreground">Class Position</h3>
              </div>
              <p className="text-4xl font-bold text-accent">{result.position || '-'}</p>
              <p className="text-sm text-muted-foreground mt-2">
                Out of {classRankings.length} students
              </p>
            </Card>

            <Card className="p-6 border-border">
              <div className="flex items-center gap-3 mb-4">
                <TrendingUp className="w-6 h-6 text-secondary" />
                <h3 className="font-semibold text-foreground">Time Taken</h3>
              </div>
              <p className="text-2xl font-bold text-primary">{result.duration} min</p>
              <p className="text-sm text-muted-foreground mt-2">
                Exam duration: {exam.duration} min
              </p>
            </Card>
          </motion.div>

          {/* Performance Metrics */}
          <motion.div variants={itemVariants}>
            <Card className="p-6 border-border space-y-4">
              <h3 className="font-semibold text-foreground flex items-center gap-2">
                <Award className="w-5 h-5 text-secondary" />
                Performance
              </h3>

              <div className="space-y-3">
                <div>
                  <div className="flex justify-between text-sm mb-1">
                    <span className="text-muted-foreground">Accuracy</span>
                    <span className="font-semibold text-primary">
                      {Math.round((result.marksObtained / result.totalMarks) * 100)}%
                    </span>
                  </div>
                  <div className="h-2 bg-border rounded-full overflow-hidden">
                    <div
                      className="h-full bg-secondary transition-all duration-1000"
                      style={{
                        width: `${(result.marksObtained / result.totalMarks) * 100}%`,
                      }}
                    />
                  </div>
                </div>

                <div>
                  <div className="flex justify-between text-sm mb-1">
                    <span className="text-muted-foreground">Class Average</span>
                    <span className="font-semibold text-primary">
                      {Math.round(
                        classRankings.reduce((sum, r) => sum + r.percentage, 0) /
                          classRankings.length
                      )}%
                    </span>
                  </div>
                  <div className="h-2 bg-border rounded-full overflow-hidden">
                    <div
                      className="h-full bg-primary/50"
                      style={{
                        width: `${Math.round(
                          classRankings.reduce((sum, r) => sum + r.percentage, 0) /
                            classRankings.length
                        )}%`,
                      }}
                    />
                  </div>
                </div>
              </div>
            </Card>
          </motion.div>
        </motion.div>

        {/* Charts */}
        <motion.div
          initial="hidden"
          animate="visible"
          variants={containerVariants}
          className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-8"
        >
          {/* Performance Trend */}
          <motion.div variants={itemVariants}>
            <Card className="p-6 border-border">
              <h3 className="font-semibold text-foreground mb-4">Performance Trend</h3>
              <ResponsiveContainer width="100%" height={300}>
                <LineChart data={performanceHistory}>
                  <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--border))" />
                  <XAxis dataKey="exam" stroke="hsl(var(--muted-foreground))" />
                  <YAxis stroke="hsl(var(--muted-foreground))" domain={[0, 100]} />
                  <Tooltip
                    contentStyle={{
                      backgroundColor: 'hsl(var(--card))',
                      border: '1px solid hsl(var(--border))',
                      borderRadius: '0.5rem',
                    }}
                  />
                  <Line
                    type="monotone"
                    dataKey="score"
                    stroke="hsl(var(--secondary))"
                    strokeWidth={2}
                    dot={{ fill: 'hsl(var(--primary))', r: 4 }}
                  />
                </LineChart>
              </ResponsiveContainer>
            </Card>
          </motion.div>

          {/* Score Distribution */}
          <motion.div variants={itemVariants}>
            <Card className="p-6 border-border">
              <h3 className="font-semibold text-foreground mb-4">Class Score Distribution</h3>
              <ResponsiveContainer width="100%" height={300}>
                <PieChart>
                  <Pie
                    data={scoreDistribution}
                    cx="50%"
                    cy="50%"
                    labelLine={false}
                    label={({ name, value }) => `${name}: ${value}`}
                    outerRadius={80}
                    fill="#8884d8"
                    dataKey="value"
                  >
                    {scoreDistribution.map((entry, index) => (
                      <Cell key={`cell-${index}`} fill={entry.fill} />
                    ))}
                  </Pie>
                  <Tooltip />
                </PieChart>
              </ResponsiveContainer>
            </Card>
          </motion.div>
        </motion.div>

        {/* Class Rankings */}
        <motion.div
          initial="hidden"
          animate="visible"
          variants={containerVariants}
        >
          <motion.div variants={itemVariants}>
            <Card className="border-border">
              <div className="p-6 border-b border-border">
                <h3 className="text-lg font-semibold text-foreground">Class Rankings</h3>
              </div>
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="border-b border-border bg-muted/50">
                      <th className="px-6 py-4 text-left font-semibold text-foreground">
                        Position
                      </th>
                      <th className="px-6 py-4 text-left font-semibold text-foreground">
                        Student
                      </th>
                      <th className="px-6 py-4 text-center font-semibold text-foreground">
                        Score
                      </th>
                      <th className="px-6 py-4 text-center font-semibold text-foreground">
                        %
                      </th>
                      <th className="px-6 py-4 text-center font-semibold text-foreground">
                        Grade
                      </th>
                    </tr>
                  </thead>
                  <tbody>
                    {classRankings.map((rank, index) => (
                      <tr
                        key={rank.id}
                        className={`border-b border-border ${
                          rank.studentId === result.studentId
                            ? 'bg-primary/5'
                            : 'hover:bg-muted/50'
                        } transition-colors`}
                      >
                        <td className="px-6 py-4">
                          <Badge
                            variant={
                              index === 0 ? 'default' : 'outline'
                            }
                            className="text-center justify-center w-8"
                          >
                            {index + 1}
                          </Badge>
                        </td>
                        <td className="px-6 py-4 font-medium text-foreground">
                          {rank.studentName}
                          {rank.studentId === result.studentId && (
                            <span className="ml-2 text-xs text-primary">(You)</span>
                          )}
                        </td>
                        <td className="px-6 py-4 text-center text-primary font-semibold">
                          {rank.marksObtained}/{rank.totalMarks}
                        </td>
                        <td className="px-6 py-4 text-center text-secondary font-semibold">
                          {rank.percentage}%
                        </td>
                        <td className="px-6 py-4 text-center">
                          <Badge className={getGradeColor(rank.percentage)}>
                            {getGrade(rank.percentage)}
                          </Badge>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </Card>
          </motion.div>
        </motion.div>

        {/* Action Buttons */}
        <motion.div
          initial="hidden"
          animate="visible"
          variants={itemVariants}
          className="flex gap-4 justify-center mt-8"
        >
          <Button
            onClick={onDone}
            className="bg-primary hover:bg-primary/90 gap-2 px-8"
          >
            <Home className="w-4 h-4" />
            Back to Dashboard
          </Button>
          <Button
            variant="outline"
            className="gap-2 border-border"
          >
            <DownloadCloud className="w-4 h-4" />
            Download Results
          </Button>
        </motion.div>
      </div>
    </div>
  );
}
